import React from "react";

import { AttributeType } from "@energy-advice/easas-api";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { GanttRangesProps, IGanttRangeRelative } from "components/ComponentTypeTypes";
import TableSkeleton from "components/skeletons/TableSkeleton";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 as uuidv4 } from "uuid";
import { getHexRandomColor } from "../../utils";

const GanttRangesRelative: React.FC<GanttRangesProps> = ({
  data,
  loading,
  currentConfig,
  setCurrentConfig,
}) => {
  if (loading) return <TableSkeleton />;

  const handleRangeChange = (
    body: IGanttRangeRelative,
    e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent
  ) => {
    setCurrentConfig(prev => ({
      ...prev,
      ganttRanges: prev.ganttRanges.map(ob =>
        ob.id === body.id ? { ...ob, [e.target.name]: e.target.value } : ob
      ),
    }));
  };

  const renderTitleInput = (body: IGanttRangeRelative, field: keyof IGanttRangeRelative) => (
    <InputText
      value={(body[field] as string) ?? ""}
      type="text"
      className="p-inputtext-sm w-11rem"
      placeholder="Write value"
      name={field}
      onChange={e => handleRangeChange(body, e)}
    />
  );

  const renderDropdown = (body: IGanttRangeRelative, field: keyof IGanttRangeRelative) => (
    <Dropdown
      value={body[field] ?? ""}
      showClear={field === "label"}
      options={data?.data?.attributes.filter(attr => attr.type !== AttributeType.DATETIME)}
      optionLabel="displayName"
      optionValue="referenceName"
      placeholder={T("word.select-one")}
      name={field}
      className="p-inputtext-sm w-11rem"
      onChange={e => handleRangeChange(body, e)}
    />
  );

  const renderSwitch = (body: IGanttRangeRelative, field: keyof IGanttRangeRelative) => (
    <InputSwitch
      checked={(body[field] as boolean) ?? false}
      name={field as string}
      onChange={e => handleRangeChange(body, e)}
    />
  );

  const renderColorPicker = (body: IGanttRangeRelative, field: keyof IGanttRangeRelative) => (
    <ColorPicker
      name={field}
      value={body[field] as string}
      className="p-inputtext-sm w-full"
      onValueChange={e => handleRangeChange(body, e)}
    />
  );

  const deleteBtn = (body: IGanttRangeRelative) => (
    <Button
      type="button"
      icon="pi pi-trash"
      className="p-button-danger p-button-text p-button-rounded p-button-raised"
      onClick={() =>
        setCurrentConfig(prev => ({
          ...prev,
          ganttRanges: prev.ganttRanges.toSpliced(
            prev.ganttRanges.findIndex(ob => ob.id === body.id),
            1
          ),
        }))
      }
    />
  );

  const handleCreateNewRange = () => {
    const initialRange: IGanttRangeRelative = {
      id: uuidv4(),
      color: getHexRandomColor(),
      endDateColumn: null,
      startDateColumn: null,
      displayValue: "",
      valueInEntry: "",
    };
    setCurrentConfig(prev => ({
      ...prev,
      ganttRanges: [
        ...prev.ganttRanges,
        {
          id: initialRange.id,
          color: initialRange.color,
          startDateColumn: null,
          endDateColumn: null,
          title: null,
          valueInEntry: initialRange.valueInEntry,
          displayValue: initialRange.displayValue,
        },
      ],
    }));
  };

  return (
    <>
      <DataTable value={currentConfig.ganttRanges} style={{ width: "100%" }} size="small">
        <Column
          field="ValueInEntryColumn"
          header={T("label.value-in-entry")}
          className="p-1"
          style={{ width: "20%" }}
          body={(body: IGanttRangeRelative) => renderTitleInput(body, "valueInEntry")}
        />
        <Column
          field="displayValue"
          header={T("label.display-value")}
          className="p-1"
          style={{ width: "20%" }}
          body={(body: IGanttRangeRelative) => renderTitleInput(body, "displayValue")}
        />
        <Column
          field="startColumn"
          header={T("word.start-date")}
          className="p-1"
          style={{ width: "10%" }}
          body={body => renderDropdown(body, "startDateColumn")}
        />
        <Column
          field="endColumn"
          header={T("word.end-date")}
          className="p-1"
          style={{ width: "10%" }}
          body={body => renderDropdown(body, "endDateColumn")}
        />
        <Column
          field="label"
          header={T("word.label")}
          className="p-1"
          style={{ width: "10%" }}
          body={(body: IGanttRangeRelative) => renderDropdown(body, "label")}
        />
        <Column
          field="labelColor"
          header={T("label.label-color")}
          className="p-1"
          style={{ width: "10%" }}
          body={(body: IGanttRangeRelative) => renderColorPicker(body, "labelColor")}
        />
        <Column
          field="useTransparency"
          header={T("label.transparency")}
          className="p-1"
          style={{
            textAlign: "center",
          }}
          body={body => renderSwitch(body, "useTransparency")}
        />
        <Column
          field="color"
          header={T("word.color")}
          className="p-1"
          style={{ width: "10%" }}
          body={body => renderColorPicker(body, "color")}
        />
        <Column body={deleteBtn} />
      </DataTable>
      <div className="flex mt-3">
        <Button
          type="button"
          icon="pi pi-plus"
          className="p-button-outlined p-button-rounded p-button-success mt-3 ml-3"
          title={T("action.add")}
          onClick={handleCreateNewRange}
        />
      </div>
    </>
  );
};

export default GanttRangesRelative;
